import * as React from 'react';
import { IMaskInput } from 'react-imask';
import { NumericFormat, NumericFormatProps } from 'react-number-format';
import TextField from '@mui/material/TextField';
import { Box, styled, Typography } from '@mui/material';
import { colors } from '../../constants/colors';
import HelperText from './helperText';

interface CustomProps {
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
}

const CustomTextField = styled(TextField)<{ inputHeight?: any }>(
    ({ theme, inputHeight }) => ({
        width: "100%",
        "&:disabled": {
            backgroundColor: "red"
        },
        ".MuiOutlinedInput-root": {
            height: inputHeight || 43,
            borderRadius: 6,
            "&:not(.Mui-disabled)": {
                backgroundColor: colors.white,
            },
            ".MuiOutlinedInput-notchedOutline, &.Mui-disabled .MuiOutlinedInput-notchedOutline":
            {
                top: "-5px !important",
                borderColor: `${colors.black_12}`,
            },
            "&:hover": {
                borderColor: `${colors.black_12}`,
                ".MuiOutlinedInput-notchedOutline": {
                    borderColor: `${colors.black_12}`,
                },
            },
            "&.Mui-focused": {
                ".MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.themeMainColor,
                },
            },
            "&.Mui-error": {
                ".MuiOutlinedInput-notchedOutline": {
                    border: `2px solid ${colors.red} !important`,
                },
            },
            ".MuiOutlinedInput-notchedOutline": {
                borderWidth: "1px !important",
            },
        },
        ".MuiInputLabel-root, .MuiInputLabel-root span": {
            fontSize: 16,
            color: `${colors.black_60} !important`,
            "&.MuiInputLabel-root": {
                paddingLeft: 1,
            },
            backgroundColor: "transparent !important",
            "&:before": {
                content: '""',
                position: "absolute",
                width: "100%",
                height: 3,
                top: "50.5%",
                zIndex: -1,
            },
            '&[data-shrink="true"]': {
                "&.Mui-focused": {
                    color: `${colors.themeMainColor} !important`,
                },
                "&:before": {
                    backgroundColor: colors.white,
                },
                span: {
                    color: `${colors.red} !important`,
                },
            },
        },
        ".MuiOutlinedInput-input": {
            height: inputHeight || 43,
            fontSize: 16,
            color: `${colors.textPrimary} !important`,
            textOverflow: "ellipsis",
            "-webkit-text-fill-color": "unset !important",
            padding: "0 1rem",
            "&.Mui-disabled": {
                color: `${colors.black_60} !important`,
            },
            "&::placeholder": {
                opacity: 1,
                color: `${colors.placeholder} !important`,
            },
        },
        "&.filed-error": {
            '.MuiInputLabel-root[data-shrink="true"], .MuiInputLabel-root[data-shrink="true"] span':
            {
                color: `${colors.red} !important`,
            },
        },
        "input[type=number]": {
            MozAppearance: "textfield",
            "-moz-appearance": "textfield",
        },
        "input::-webkit-outer-spin-button, input::-webkit-inner-spin-button": {
            marginLeft: "0.5rem",
        },
        "&.hide-control": {
            "input[type=number], input::-webkit-outer-spin-button, input::-webkit-inner-spin-button":
            {
                "-webkit-appearance": "none",
                appearance: "none",
                marginLeft: 0,
            },
        },
        ".MuiFormHelperText-root": {
            marginLeft: 0,
        },
    })
);

const TextMaskCustom = React.forwardRef<HTMLInputElement, CustomProps>(
    function TextMaskCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                mask="(#00) 000-0000"
                definitions={{
                    '#': /[1-9]/,
                }}
                inputRef={ref}
                onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
                overwrite
            />
        );
    },
);
const TextMaskThaiIdCustom = React.forwardRef<HTMLInputElement, CustomProps>(
    function TextMaskCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                mask="0 0000 00000 00 0"
                definitions={{
                    '#': /[1-9]/,
                }}
                inputRef={ref}
                onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
                overwrite
            />
        );
    },
);
const TextMaskNumberCustom = React.forwardRef<HTMLInputElement, CustomProps>(
    function TextMaskCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                mask={Number}
                definitions={{
                    '#': /[0-9]/,
                }}
                inputRef={ref}
                onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
                overwrite
            />
        );
    },
);
const TextMaskNumberNotDigit = React.forwardRef<HTMLInputElement, CustomProps>(
    function TextMaskCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <IMaskInput
                {...other}
                mask={/^\d+$/}
                inputRef={ref}
                onAccept={(value: any) => onChange({ target: { name: props.name, value } })}
                overwrite
            />
        );
    },
);

const TextMaskIntegerWithLimit = React.forwardRef<HTMLInputElement, CustomProps & { maxNumber?: number; minNumber?: number }>(
    function TextMaskIntegerWithLimit(props, ref) {
        const { onChange, ...other } = props as any;
        const maxNumber = other?.maxNumber;
        const minNumber = other?.minNumber ?? 0;

        return (
            <IMaskInput
                {...other}
                // ใช้ Number mask เพื่อให้ตั้ง max/min ได้ และรับเฉพาะจำนวนเต็ม
                mask={Number}
                scale={0}            // ✅ ไม่มีทศนิยม
                signed={false}       // ✅ ไม่ติดลบ
                min={typeof minNumber === 'number' ? minNumber : undefined}
                max={typeof maxNumber === 'number' ? maxNumber : undefined}
                // ไม่ใส่ thousandsSeparator เพื่อให้ value ที่ส่งออกมาไม่มีคอมมา
                inputRef={ref}
                normalizeZeros={false}
                onAccept={(value: any) =>
                    onChange({ target: { name: props.name, value: String(value ?? '') } })
                }
                overwrite
            />
        );
    },
);

const NumericFormatCustom = React.forwardRef<NumericFormatProps, CustomProps>(
    function NumericFormatCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                decimalScale={2}
                thousandSeparator
                valueIsNumericString
            // prefix="$"
            />
        );
    },
);
const NumericFormatCustomOneDigit = React.forwardRef<NumericFormatProps, CustomProps>(
    function NumericFormatCustom(props, ref) {
        const { onChange, ...other } = props;
        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                decimalScale={1}
                thousandSeparator
                valueIsNumericString
            // prefix="$"
            />
        );
    },
);
const LatLngFormat = React.forwardRef<HTMLInputElement, CustomProps>(
    function LatLngFormatCustom(props, ref) {
        const { onChange, ...other } = props;

        return (
            <NumericFormat
                {...other}
                getInputRef={ref}
                onValueChange={(values) => {
                    onChange({
                        target: {
                            name: props.name,
                            value: values.value,
                        },
                    });
                }}
                decimalScale={12}
                fixedDecimalScale
                valueIsNumericString
                suffix=""
            />
        );
    }
)


export type MaskProps = {
    heading?: string;
    mode: 'amount' | 'phone' | 'thai-id' | 'number' | 'latlng' | 'full_number' | 'one_digit';
    headingAlign?: React.CSSProperties['textAlign'];
    headingNormal?: boolean;
    required?: boolean;
    markRequired?: boolean;
    value?: string;
    onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    maxLength?: number;
    disabledAutoComplete?: boolean;
    endAdornment?: any;
    startAdornment?: any;
    helperText?: string;
    placeholder?: string;
    hideControl?: boolean;
    disabled?: boolean;
    disableColor?: string;

    maxNumber?: number;
    minNumber?: number;
};

const InputTextMask: React.FC<MaskProps> = (props: MaskProps) => {
    const { onChange, value, mode, disabled } = props;
    const helperTextRef = React.useRef<HTMLInputElement>(null);

    React.useEffect(() => {
        if (props.helperText && helperTextRef.current) {
            helperTextRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            helperTextRef.current.focus();
        }
    }, [props.helperText]);

    return (
        <Box sx={{ display: 'flex', flexDirection: "column" }}>
            {
                props.heading && (
                    <div
                        className="pb-2"
                        style={{ textAlign: props.headingAlign || "left" }}
                    >
                        <Typography variant={props.headingNormal ? 'subtitle1' : 'body1'}>
                            {props.heading}{" "} {props.required || props.markRequired ? <span style={{ color: colors.red }}>*</span> : ""}
                        </Typography>
                    </div>
                )
            }
            <CustomTextField
                disabled={disabled}
                ref={helperTextRef}
                value={value}
                onChange={onChange}
                name="numberformat"
                id="formatted-numberformat-input"
                placeholder={props.placeholder}
                className={`${props.helperText ? "filed-error" : ""} ${props.hideControl ? "hide-control" : ""
                    }`}
                error={props.helperText !== "" && props.helperText !== undefined}
                inputProps={{
                    maxLength: props.maxLength,
                    autoComplete: props.disabledAutoComplete ? "off" : "on",
                    maxNumber: props.maxNumber,
                    minNumber: props.minNumber,
                }}
                inputRef={(input: HTMLInputElement | null) => {
                    if (props.helperText && input) {
                        input.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }
                }}
                sx={{
                    ".MuiOutlinedInput-root": {
                        backgroundColor: props.disabled ? props.disableColor ? props.disableColor : colors.black_12 : "",
                    },
                }}
                InputProps={{
                    endAdornment: props.endAdornment,
                    startAdornment: props.startAdornment,
                    inputComponent:
                        mode === 'thai-id' ?
                            TextMaskThaiIdCustom as any :
                            mode === 'amount' ?
                                NumericFormatCustom as any :
                                mode === 'one_digit' ?
                                    NumericFormatCustomOneDigit as any :
                                    mode === 'phone' ?
                                        TextMaskCustom as any :
                                        mode === 'latlng' ?
                                            LatLngFormat :
                                            mode === 'full_number' ?
                                                TextMaskIntegerWithLimit :
                                                mode === 'number' ?
                                                    TextMaskNumberCustom as any : TextMaskCustom as any
                }}
                helperText={
                    props.helperText ? <HelperText label={props.helperText} /> : ""
                }
                variant="outlined"
            />


        </Box>
    );
}

export default InputTextMask